import React, { useEffect, useState, useRef } from "react";
import "./App.css";
import { ReactTagManager } from "react-gtm-ts";

const tagManagerArgs = {
  code: "GTM-WZQPMC7",
};

function App() {
  ReactTagManager.init(tagManagerArgs);
  const intercomInitialized = useRef(false);
  const BASE_URL = "https://wix.certifiedcode.io/ecwid-smooth-scroll";
  const [instance, setInstance] = React.useState({} as any);
  const [isEnabled, setIsEnabled] = useState(false);
  const [speed, setSpeed] = useState(30);
  const [smooth, setSmooth] = useState(12);

  useEffect(() => {
    if (!intercomInitialized.current && instance.intercomToken && instance.storeProfile && instance.app) {
      const name = instance.storeProfile.account.accountName;
      const registrationDate = new Date(
        instance.storeProfile.account.registrationDate
      );
      const created_at = Math.floor(registrationDate.getTime() / 1000);
      const phone = instance.storeProfile.company.phone;
      const email = instance.storeProfile.account.accountEmail;
      const utm_source = instance.app.client_id;
      const options = {
        email,
        name,
        created_at,
        phone,
        user_hash: instance.intercomToken,
        utm_source,
      };
      window.Intercom("boot", options);
      intercomInitialized.current = true;
    }
  }, [instance]);

  useEffect(() => {
    try {
      // Initialize the application
      window.EcwidApp.init({
        app_id: "smooth-scroll",
        autoloadedflag: true,
        autoheight: false,
      });

      getInstance();

      // Function to update height
      const updateHeight = () => {
        var body = document.body,
          html = document.documentElement;

        var height = Math.max(
          body.scrollHeight,
          body.offsetHeight,
          html.clientHeight,
          html.scrollHeight,
          html.offsetHeight
        );

        window.EcwidApp.setSize({ height: height });
      };

      // Initial height update
      updateHeight();

      // Set up intervals for both instance and height updates
      const instanceIntervalId = setInterval(getInstance, 10000);
      const heightIntervalId = setInterval(updateHeight, 1000);

      // Add settings fetch after getInstance
      if (instance) {
        getSettings();
      }

      // Cleanup intervals on component unmount
      return () => {
        clearInterval(instanceIntervalId);
        clearInterval(heightIntervalId);
      };
    } catch (error) {
      console.error(error);
    }
  }, []);

  function getInstance() {
    fetch(
      BASE_URL + "/_functions/@certifiedcode/ecwid-repluggable-backend/auth",
      {
        headers: {
          Authorization: new URLSearchParams(window.location.search).get(
            "payload"
          )!,
        },
      }
    )
      .then((response) => response.json())
      .then((payload) => {
        setInstance(payload);
        window.EcwidApp.ready();
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  }

  function getSettings() {
    fetch(
      BASE_URL +
        "/_functions/@certifiedcode/ecwid-repluggable-backend/settings",
      {
        method: "GET",
        headers: {
          Authorization: new URLSearchParams(window.location.search).get(
            "payload"
          )!,
        },
      }
    )
      .then((response) => response.json())
      .then((data) => {
        console.log(data);
        setIsEnabled(data.isEnabled);
        setSpeed(data.config?.speed ?? 30);
        setSmooth(data.config?.smooth ?? 12);
      })
      .catch(() => {});
  }

  const handleToggle = () => {
    if (!instance?.subscription) {
      // Open upgrade page if not subscribed
      window.open(
        instance?.storeProfile
          ? encodeURI(
              "https://www.certifiedcode.us/upgrade/apps?checkout=" +
                JSON.stringify({
                  settings: {
                    allowLogout: false,
                    allowDiscountRemoval: false,
                  },
                  items: [
                    { priceId: "pri_01jm68rt8xa5cqc0w3ng9fdhek", quantity: 1 },
                  ],
                  customer: {
                    email: instance.storeProfile.account.accountEmail,
                    business: {
                      name: instance.storeProfile.account.accountName,
                    },
                    address: {
                      countryCode: instance.storeProfile.company.countryCode,
                      postalCode: instance.storeProfile.company.postalCode,
                      city: instance.storeProfile.company.city,
                    },
                  },
                  customData: { store_id: `${instance.store_id}` },
                })
            )
          : undefined,
        "_blank"
      );
    } else {
      // First update the API, then update local state when successful
      fetch(
        BASE_URL +
          "/_functions/@certifiedcode/ecwid-repluggable-backend/settings",
        {
          method: "POST",
          headers: {
            Authorization: new URLSearchParams(window.location.search).get(
              "payload"
            )!,
          },
          body: JSON.stringify({
            isEnabled: !isEnabled, // Send the new state
            config: {},
          }),
        }
      )
        .then((response) => response.json())
        .then((data) => {
          console.log(data);
          setIsEnabled(data.isEnabled);
        })
        .catch(() => {});
    }
  };

  const handleSettingsChange = (type: 'speed' | 'smooth', value: number) => {
    const newValue = Math.max(0, Math.min(100, value)); // Clamp between 0 and 100
    
    if (type === 'speed') {
      setSpeed(newValue);
    } else {
      setSmooth(newValue);
    }

    // Update settings on server
    if (instance?.subscription) {
      fetch(
        BASE_URL +
          "/_functions/@certifiedcode/ecwid-repluggable-backend/settings",
        {
          method: "POST",
          headers: {
            Authorization: new URLSearchParams(window.location.search).get(
              "payload"
            )!,
          },
          body: JSON.stringify({
            isEnabled,
            config: {
              speed: type === 'speed' ? newValue : speed,
              smooth: type === 'smooth' ? newValue : smooth,
            },
          }),
        }
      )
        .then((response) => response.json())
        .catch(() => {});
    }
  };

  return (
    <div className="settings-page cf">
      <div className="settings-page__header">
        <div className="settings-page__titles settings-page__titles--left">
          <h1 className="settings-page__title">Smooth Scroll</h1>
          <div className="settings-page__subtitle">
            Improved scrolling experience that feels better
          </div>
        </div>

        <div className="named-area">
          <div className="named-area__header">
            <div className="named-area__titles">
              <div className="named-area__title">Smooth Scroll overview</div>
            </div>
            <div className="named-area__description">
              Transform your browsing experience with premium smooth scrolling
              features. Plans start from $10.00/month.
            </div>
          </div>
          <div className="named-area__body">
            <div
              className="a-card a-card--normal"
              style={{ backgroundColor: "#2964de" }}
            >
              <div className="a-card__paddings">
                <div className="upgrade-element upgrade-element--inverted">
                  <div className="upgrade-element__core">
                    <div className="upgrade-element__data">
                      <div className="upgrade-element__title">
                        Enhance Your Scrolling Experience Today
                      </div>
                      <div className="upgrade-element__content">
                        <div className="upgrade-element__text">
                          Tired of choppy scrolling that disrupts your flow?
                          Smooth Scroll is here to elevate your browsing
                          experience with silky-smooth animations.
                        </div>
                        <div className="upgrade-element__action">
                          {instance.store_id ? (
                            <div className="status-block__primary-action">
                              <label className="checkbox big">
                                <input
                                  type="checkbox"
                                  checked={isEnabled}
                                  onChange={handleToggle}
                                />
                                <div>
                                  <div></div>
                                </div>
                              </label>
                            </div>
                          ) : (
                            <div className="loader"></div>
                          )}
                        </div>
                      </div>
                      <div className="upgrade-element__note">
                        <div>
                          {instance?.subscription
                            ? "Click to toggle smooth scrolling"
                            : "Upgrade to enable smooth scrolling"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Add new settings section */}
        {instance?.subscription && isEnabled && (
          <div className="named-area">
            <div className="named-area__header">
              <div className="named-area__titles">
                <div className="named-area__title">Scroll Settings</div>
              </div>
              <div className="named-area__description">
                Customize your scrolling experience
              </div>
            </div>
            <div className="named-area__body">
              <div className="a-card a-card--normal">
                <div className="a-card__paddings">
                  <div className="fieldset">
                    <div className="fieldset__title">Scroll Speed</div>
                    <div className="fieldset__field-wrapper">
                      <div className="field field--medium">
                        <label className="field__label">Speed (0-100)</label>
                        <input
                          type="number"
                          className="field__input"
                          value={speed}
                          onChange={(e) => handleSettingsChange('speed', parseInt(e.target.value))}
                          min="0"
                          max="100"
                        />
                        <div className="field__placeholder">Enter speed value</div>
                        <div className="fieldset__field-suffix">px/s</div>
                      </div>
                    </div>
                    <div className="fieldset__description">
                      Controls how fast the page scrolls. Higher values mean faster scrolling.
                    </div>
                  </div>

                  <div className="fieldset" style={{ marginTop: '20px' }}>
                    <div className="fieldset__title">Smoothness</div>
                    <div className="fieldset__field-wrapper">
                      <div className="field field--medium">
                        <label className="field__label">Smoothness (0-100)</label>
                        <input
                          type="number"
                          className="field__input"
                          value={smooth}
                          onChange={(e) => handleSettingsChange('smooth', parseInt(e.target.value))}
                          min="0"
                          max="100"
                        />
                        <div className="field__placeholder">Enter smoothness value</div>
                        <div className="fieldset__field-suffix">frames</div>
                      </div>
                    </div>
                    <div className="fieldset__description">
                      Controls how smooth the scrolling feels. Higher values create smoother animations.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="named-area">
          <div className="named-area__header">
            <div className="named-area__titles">
              <div className="named-area__title">Key Features</div>
            </div>
            <div className="named-area__description">
              Discover how our smooth scrolling solution can transform your
              browsing experience
            </div>
          </div>
          <div className="named-area__body">
            <div className="a-card a-card--normal">
              <div className="a-card__paddings">
                <div>
                  <div className="promo-row">
                    <div className="promo-row__content">
                      <ul className="titled-items-list">
                        <li className="titled-item titled-items-list__item">
                          <div className="titled-item__title">
                            Butter-smooth Scrolling
                          </div>
                          <div className="titled-item__content">
                            Enjoy seamless navigation on webpages and documents
                            with our advanced animation engine
                          </div>
                        </li>
                        <li className="titled-item titled-items-list__item">
                          <div className="titled-item__title">
                            Universal Compatibility
                          </div>
                          <div className="titled-item__content">
                            Works flawlessly with all your favorite browsers and
                            applications
                          </div>
                        </li>
                        <li className="titled-item titled-items-list__item">
                          <div className="titled-item__title">
                            Lightweight Design
                          </div>
                          <div className="titled-item__content">
                            Runs seamlessly in the background without
                            compromising site performance
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="named-area__footer">
            <div className="named-area__footer-description"></div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
